import { useQuery } from '@tanstack/react-query';
import { useCustomInstance } from '@/lib/use-custom-instance';

export interface PublicFooter {
  /** Server-sanitised footer HTML for the resolved country (or the Default). */
  contentHtml: string;
  /**
   * Server-sanitised HTML collapsed behind the "Read More" link (ARC-171).
   * Null = the footer has no Read More section and the link must not render.
   */
  readMoreHtml: string | null;
}

/**
 * Published footer for the resolved country, falling back to the Default
 * (server-side). `countryCode` comes from `useTerritory()` when known
 * (KYC-verified players); omit it for signed-out / unresolved players to get
 * the Default.
 *
 * INTERIM data layer (mirrors lib/documents/useDocuments): once the api ships
 * the `/footer` route to the OpenAPI spec, `yarn generate-api` will emit a typed
 * hook in `src/lib/endpoints.ts` — both funnel through the same
 * `useCustomInstance` mutator, so the swap is mechanical.
 *
 * A short staleTime + refetch-on-focus lets a publish in admin go live without a
 * deploy within ~a few minutes.
 */
export function useFooter(countryCode?: string) {
  const request = useCustomInstance<PublicFooter>();
  return useQuery({
    queryKey: ['/footer', countryCode ?? null],
    queryFn: () =>
      request({
        url: '/footer',
        method: 'GET',
        params: countryCode ? { country: countryCode } : undefined,
      }),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}
